<script>
  import { createEventDispatcher } from 'svelte'
  import Row from './row.svelte'
  import Block from './block.svelte'
  import Text from './text.svelte'
  import TextInput from './textinput.svelte'
  import CheckBox from './checkbox.svelte'
  import Icon from './icon.svelte'
  import { calcPxNum } from './utils'

  const dispatch = createEventDispatcher()

  export let border = 1
  export let editBorderColor = 'black'
  export let fontSize = '1rem'
  export let lineHeight = '1.3rem'
  export let rowHeight = '2.4rem'
  export let backgroundColor = 'transparent'
  export let headerBgColor = 'ghostwhite'
  export let hoverBgColor = 'ghostwhite'
  export let checkable = false
  export let canCheckAll = true
  export let draggable = false
  export let dragAuto = true
  export let resizable = false
  export let wrap = false

  // rowEditable conflict with cell edit and col.editable
  // caller should save old row value for cancel edit
  export let rowEditable = false 

  export let columns = [
    {name: 'id', title: 'ID', width: 80},
    {name: 'name', title: '名称', width: 180, editable: true},
    {name: 'desc', title: '描述', width: 180, component: Text},
  ]
  export let rows = [
    {id: 1, name: 'test1', desc: 'xxx1'},
    {id: 2, name: 'test2', desc: 'xxx2'},
    {id: 3, name: 'test3', desc: 'xxx3'},
    {id: 4, name: 'test4', desc: 'xxx4'},
    {id: 5, name: 'test5', desc: 'xxx5'},
  ]

  export let editRowNo = -1
  export let editColName = ''
  export let editValue = ''

  let beforeValue = ''
  let hoverRowNo = -1
  let cellFocusing = false
  let draggingRow = null
  let draggingRowNo = -1
  let dragTargetRowNo = -1

  $: totalWidth = function() {
    let sum = 0
    if (checkable) sum += 24 + 1// 1 means border width
    if (draggable) sum += 24 + 1
    columns.forEach(col => sum += col.width + 1)
    return sum - 1
  }()

  function resizeColumn(n) {
    return (e) => {
      let startX = e.clientX
      let baseWidth = columns[n].width // clientWidth
      document.onmousemove = function(e){
        let offset = e.clientX - startX
        columns[n].width = `calc(${calcPxNum(baseWidth)} + ${offset}px)`
        //columns[n].width = baseWidth + offset
      }
      document.onmouseup = function(e){
        e.stopPropagation()
        document.onmousemove = null
        document.onmouseup = null
      }
      return false
    }
  }

  function resetCellEdit() {
    cellFocusing = false
    editRowNo = -1
    editColName = ''
  }

  export function cellEditBegin(row, rowNo, colName) {
    editRowNo = rowNo
    editColName = colName
    beforeValue = row[colName]
    editValue = beforeValue
    cellFocusing = true
  }

  function cellEditDone(row, rowNo, col) {
    resetCellEdit()
    row[col] = editValue
    dispatch('editcell', {
      row: row,
      rowNo: rowNo,
      column: col,
      beforeValue: beforeValue,
      value: editValue,
    })
  }

  function checkAll(checked) {
    for (let i = 0; i < rows.length; i++) {
      rows[i]._checked = checked
    }
    dispatch('checkall', {checked: checked})
  }

  function dragStart(event, row, rowNo) {
    draggingRow = row
    draggingRowNo = rowNo
    event.dataTransfer.setDragImage(row.div, 0, 0)
  }

  function dragEnter(row, rowNo) {
    if (!draggable) return
    dragTargetRowNo = rowNo
  }

  function dragDrop(row, rowNo) {
    if (!draggable) return
    if (draggingRow != rowNo) {
      if (dragAuto) {
        rows.splice(draggingRowNo, 1)  // remove before insert
        rows.splice(rowNo, 0, draggingRow)
        rows = rows
      }
      dispatch('dragrow', {dragRowNo: draggingRowNo, targetRowNo: rowNo})
    }
    draggingRow = null
    draggingRowNo = -1
    dragTargetRowNo = -1
  }
</script>

<Block name=table display=flex direction=column>
  <Row>
    {#if draggable}
    <Block
      name=drag
      {border}
      width=24
      minWidth=24
      height={rowHeight}
      {fontSize}
      backgroundColor={headerBgColor}
    >
    </Block>
    {/if}

    {#if checkable}
    <Block name=check
      display=flex
      align=center
      xAlign=center
      topBorder={border}
      leftBorder={draggable?null:border}
      rightBorder={border}
      bottomBorder={border}
      width=40
      height={rowHeight}
      backgroundColor={headerBgColor}
      {fontSize}
    >
      {#if canCheckAll}
      <CheckBox on:change={e=>checkAll(e.detail.checked)}></CheckBox>
      {/if}
    </Block>
    {/if}
    
    {#each columns as col, colNo}
    <Block name=col
      display=flex
      xAlign=center
      height={rowHeight}
      topBorder={border}
      leftBorder={(colNo==0&&!checkable)?border:null}
      rightBorder={border}
      bottomBorder={border}
      width={col.width}
      backgroundColor={headerBgColor}
      {fontSize}
      position=relative
    >
      <Block width=0.4rem/>
      <Text>{col.title}</Text>
      <Block width=0.4rem/>
      {#if resizable}
      <Block
        position=absolute
        top=0
        right=-3
        backgroundColor=DodgerBlue 
        width=5
        height=100%
        cursor=col-resize
        zIndex=999
        on:mousedown={resizeColumn(colNo)}
      ></Block>
      {/if}
    </Block>
    
    {/each}
  </Row>

  {#each rows as row, rowNo}
  {#if row._isMerged}
  <Block
    name=cell
    leftBorder={border}
    rightBorder={border}
    bottomBorder={border}
    height={rowHeight}
    width={totalWidth}
    maxWidth={totalWidth}
    {fontSize}
    backgroundColor={(rowNo==hoverRowNo&&!cellFocusing)?hoverBgColor:backgroundColor}
  >{row.title}</Block>
  {:else if !row._hidden}
  <Block
    on:mouseenter={()=>{hoverRowNo=rowNo;dispatch('overrow',{row:row,rowNo:rowNo})}}
    on:mouseleave={()=>{hoverRowNo=-1;dispatch('outrow',{row:row,rowNo:rowNo})}}
  >
    <Block
      name=row
      display=flex
      height={wrap ? undefined : rowHeight}
      minHeight={rowHeight}
      backgroundColor={((rowNo==hoverRowNo||rowNo==dragTargetRowNo)&&!cellFocusing)?hoverBgColor:backgroundColor}
      bind:divElement={row.div}
      on:dragenter={e=>{e.preventDefault();dragEnter(row, rowNo)}}
      on:dragover={e=>e.preventDefault()}
      on:drop={dragDrop(row, rowNo)}
    >
      {#if draggable}
      <Block
        name=drag
        display=flex
        align=center
        xAlign=center
        draggable={true}
        leftBorder={border}
        rightBorder={border}
        bottomBorder={border}
        width=24
        minWidth=24
        cursor=grab
        {fontSize}
        backgroundColor=transparent
        on:dragstart={e=>dragStart(e, row, rowNo)}
      >
        {#if hoverRowNo == rowNo}
        <Icon name=drag></Icon>
        {/if}
      </Block>
      {/if}

      {#if checkable}
      <Block
        name=check
        display=flex
        align=center
        xAlign=center
        leftBorder={draggable?null:border}
        rightBorder={border}
        bottomBorder={border}
        width=40
        {fontSize}
        backgroundColor=transparent
      >
        <CheckBox
          bind:checked={row._checked}
          on:change={e=>{dispatch('checkrow', {checked: e.detail.checked,row:row, rowNo:rowNo})}}
        ></CheckBox>
      </Block>
      {/if}

      {#each columns as col, colNo}
      <Block
        name=cell
        display=flex
        xAlign=center
        leftBorder={(colNo==0&&!checkable&&!draggable)?border:null}
        rightBorder={border}
        bottomBorder={border}
        width={col.width}
        maxWidth={col.width}
        overflowY=hidden
        {fontSize}
        {lineHeight}
        backgroundColor=transparent
        canFocus={!rowEditable && col.editable}
        {...row.cellProps}
        on:mousedown={e=>{e.button==2&&e.preventDefault()}}
        on:focusin={()=>{cellEditBegin(row, rowNo, col.name)}}
        on:contextmenu={e=>{resetCellEdit();dispatch('contextmenu', {event:e, row: row, rowNo:rowNo, column:col.name})}}
      >
        {#if rowEditable && row._editing && !col.readonly && row.hasOwnProperty(col.name)}
        <TextInput
          border=1
          borderColor={editBorderColor}
          width=100%
          height=200%
          multiline={wrap}
          resize={wrap?'vertical':'none'}
          bind:value={row[col.name]}
        ></TextInput>
        {:else if !rowEditable && editRowNo == rowNo && editColName == col.name}
        <Block
          canFocus={true} height=100% width={col.width}
          on:focusout={()=>{if(cellFocusing){cellEditDone(row, rowNo, col.name)}}}
        >
          {#if col.editComponent}
          <svelte:component this={col.editComponent} {row} {rowNo} column={col.name}>{row[col.name]}</svelte:component>
          {:else}
          <TextInput
            border=1
            borderColor={editBorderColor}
            width=100%
            height=100%
            autoFocus={true}
            bind:value={editValue}
            on:keypress={e=>{if(e.key=='Enter'){cellEditDone(row, rowNo, col.name)}}}
            on:input={()=>{}}
          ></TextInput>
          {/if}
        </Block>
        {:else if col.component}
        <svelte:component this={col.component} bind:rows={rows} rowNo={rowNo} value={row[col.name]}/>
        {:else}
        <Block padding=0.4rem overflow=hidden>
        <Text textOverflow=ellipsis {wrap}>{row[col.name] || ''}</Text>
        </Block>
        {/if}
      </Block>
      {/each}
    </Block>
  </Block>
  {/if}
  {/each}

</Block>